import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { useSEO } from "@/hooks/useSEO";
import {
  BarChart3,
  TrendingUp,
  Users,
  Target,
  Zap,
  Shield,
  ArrowRight,
  MessageCircle,
  Star,
  CheckCircle2,
  Globe,
  Layers,
} from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6 },
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

/* ─── Platform Logos ─── */
const platforms = [
  { name: "Meta Ads", icon: "M", color: "#1877F2" },
  { name: "Google Ads", icon: "G", color: "#4285F4" },
  { name: "TikTok Ads", icon: "T", color: "#000000" },
  { name: "Pinterest", icon: "P", color: "#E60023" },
];

/* ─── Stats ─── */
const stats = [
  { value: "500+", label: "Marka", icon: Users },
  { value: "₺50M+", label: "Reklam Bütçesi Yönetimi", icon: TrendingUp },
  { value: "%340", label: "Ortalama ROAS", icon: BarChart3 },
  { value: "7/24", label: "Canlı Destek", icon: Shield },
];

/* ─── Features ─── */
const features = [
  {
    icon: BarChart3,
    title: "Otomatik Raporlama",
    desc: "Tüm platformlardan verileriniz otomatik olarak derlenir. Günlük, haftalık ve aylık raporlar tek tıkla.",
  },
  {
    icon: TrendingUp,
    title: "ROAS Takibi",
    desc: "Gerçek zamanlı ROAS verilerinizi takip edin. Hangi kampanya ne kazandırıyor, anında görün.",
  },
  {
    icon: Layers,
    title: "Çoklu Hesap Yönetimi",
    desc: "Meta, Google, TikTok ve Pinterest hesaplarınızı tek panelden yönetin. Sürekli platform değiştirmeye son.",
  },
  {
    icon: Target,
    title: "Hedef Kitle Analizi",
    desc: "Yapay zeka destekli kitle analizi ile en doğru hedeflemeyi yapın. Lookalike ve custom audience önerileri.",
  },
  {
    icon: Zap,
    title: "Bütçe Optimizasyonu",
    desc: "AI motorumuz bütçenizi en verimli kampanyalara otomatik dağıtır. İsrafı önleyin, getiriyi artırın.",
  },
  {
    icon: Globe,
    title: "Rakip Analizi",
    desc: "Rakiplerinizin reklam stratejilerini analiz edin. Sektör trendlerinden geri kalmayın.",
  },
];

/* ─── Testimonials ─── */
const testimonials = [
  {
    name: "Ayşe Yılmaz",
    role: "E-Ticaret Müdürü, ModaPlus",
    text: "AdsManager sayesinde 4 farklı platformdaki reklamlarımızı tek panelden yönetmeye başladık. ROAS'ımız %180 arttı.",
    rating: 5,
  },
  {
    name: "Mehmet Kaya",
    role: "Pazarlama Direktörü, TechStart",
    text: "Otomatik raporlama özelliği hayatımızı kurtardı. Eskiden saatler harcadığımız raporlar artık tek tıkla hazır.",
    rating: 5,
  },
  {
    name: "Elif Demir",
    role: "Kurucu, GreenLife Kozmetik",
    text: "AI destekli bütçe optimizasyonu ile reklam harcamalarımızı %35 azaltırken satışlarımızı ikiye katladık.",
    rating: 5,
  },
];

export default function Home() {
  useSEO();
  return (
    <Layout>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section className="relative overflow-hidden bg-[#FFFDF7] lg:min-h-[90vh] flex items-center">
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #0A1A3F 0px, #0A1A3F 1px, transparent 1px, transparent 60px),
                            repeating-linear-gradient(90deg, #0A1A3F 0px, #0A1A3F 1px, transparent 1px, transparent 60px)`,
        }} />

        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center min-w-0">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 px-3 sm:px-4 py-2 mb-6 shadow-[2px_2px_0_0_#0A1A3F]">
                <Zap size={16} className="text-[#6B8AFE] flex-shrink-0" />
                <span className="text-xs sm:text-sm font-bold text-[#0A1A3F] uppercase tracking-wider">
                  Ajans Hesabı · Aynı Gün Aktivasyon
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-[#0A1A3F] leading-[1.08] sm:leading-[1.05] tracking-tight break-words">
                Reklamlarınızı{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">Tek Panelden</span>
                  <span className="absolute bottom-1 left-0 w-full h-4 bg-[#6B8AFE]/30 -z-0" />
                </span>{" "}
                Yönetin
              </h1>

              <p className="mt-5 sm:mt-6 text-base sm:text-xl text-[#0A1A3F]/70 leading-relaxed max-w-lg">
                Meta, Google, TikTok ve Pinterest reklamlarınızı tek bir platformdan yönetin. 
                Otomatik raporlama, ROAS takibi ve AI destekli öneriler ile performansınızı maksimize edin.
              </p>

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  href="/fiyatlar"
                  className="brutalist-btn bg-[#6B8AFE] text-[#0A1A3F] text-center text-base sm:text-lg animate-pulse-shadow inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  Fiyatları İnceleyin
                  <ArrowRight size={20} />
                </Link>
                <a
                  href="https://wa.me/905320522545?text=Merhaba%2C%20reklam%20hesab%C4%B1%20kiralama%20ve%20reklam%20y%C3%B6netimi%20i%C3%A7in%20teklif%20almak%20istiyorum."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brutalist-btn bg-white text-[#0A1A3F] text-center text-base sm:text-lg inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <MessageCircle size={18} />
                  WhatsApp'tan Teklif Alın
                </a>
              </div>

              {/* Social Proof */}
              <div className="mt-8 flex items-center gap-4 min-w-0">
                <div className="flex -space-x-2 flex-shrink-0">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full border-3 border-[#0A1A3F] bg-gradient-to-br from-[#6B8AFE] to-[#0A1A3F] flex items-center justify-center text-white text-xs font-bold"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-[#0A1A3F]/60 font-medium break-words">500+ marka tarafından tercih ediliyor</p>
                </div>
              </div>
            </motion.div>

            {/* Right - Dashboard Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative min-w-0 pr-2 sm:pr-0"
            >
              <div className="border-3 border-[#0A1A3F] bg-white shadow-[6px_6px_0_0_#0A1A3F] sm:shadow-[8px_8px_0_0_#0A1A3F] p-4 sm:p-6 relative min-w-0">
                {/* Window Bar */}
                <div className="flex items-center gap-2 mb-4 pb-4 border-b-2 border-[#0A1A3F]/10">
                  <div className="w-3 h-3 rounded-full bg-red-400 border border-[#0A1A3F]/30" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400 border border-[#0A1A3F]/30" />
                  <div className="w-3 h-3 rounded-full bg-green-400 border border-[#0A1A3F]/30" />
                  <div className="ml-3 sm:ml-4 flex-1 min-w-0 bg-[#F5F5EF] border-2 border-[#0A1A3F]/10 px-3 py-1 text-xs text-[#0A1A3F]/75 font-mono truncate">
                    admin.adsmanager.com.tr/dashboard
                  </div>
                </div>

                {/* Mini Dashboard */}
                <div className="space-y-4">
                  {/* Metrics Row */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    {[
                      { label: "Toplam Harcama", value: "₺248,500", change: "+12%" },
                      { label: "Dönüşüm", value: "3,847", change: "+28%" },
                      { label: "ROAS", value: "4.2x", change: "+15%" },
                    ].map((m) => (
                      <div key={m.label} className="border-2 border-[#0A1A3F]/10 p-2 sm:p-3 min-w-0">
                        <p className="text-[10px] sm:text-xs text-[#0A1A3F]/70 font-medium uppercase leading-tight break-words">{m.label}</p>
                        <p className="text-sm sm:text-lg font-black text-[#0A1A3F] break-words">{m.value}</p>
                        <p className="text-xs font-bold text-green-700">{m.change}</p>
                      </div>
                    ))}
                  </div>

                  {/* Chart Mockup */}
                  <div className="border-2 border-[#0A1A3F]/10 p-3 sm:p-4">
                    <p className="text-xs font-bold text-[#0A1A3F]/60 uppercase mb-3">Haftalık Performans</p>
                    <div className="flex items-end gap-1.5 h-24">
                      {[40, 55, 35, 70, 65, 85, 75, 90, 60, 95, 80, 100].map((h, i) => (
                        <motion.div
                          key={i}
                          className="flex-1 bg-gradient-to-t from-[#6B8AFE] to-[#6B8AFE]/60 border border-[#0A1A3F]/20"
                          initial={{ height: 0 }}
                          animate={{ height: `${h}%` }}
                          transition={{ duration: 0.5, delay: 0.8 + i * 0.05 }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Platform Icons */}
                  <div className="flex items-center justify-between gap-2">
                    {platforms.map((p) => (
                      <div key={p.name} className="flex items-center gap-2 border-2 border-[#0A1A3F]/10 px-2.5 sm:px-3 py-2">
                        <div
                          className="w-6 h-6 flex items-center justify-center text-white text-xs font-black"
                          style={{ backgroundColor: p.color }}
                        >
                          {p.icon}
                        </div>
                        <span className="text-xs font-bold text-[#0A1A3F]/70 hidden sm:inline">{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -top-4 -right-2 sm:-right-4 bg-[#6B8AFE] text-[#0A1A3F] border-3 border-[#0A1A3F] px-3 py-1 text-xs font-black uppercase shadow-[2px_2px_0_0_#0A1A3F]">
                  CANLI
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ PLATFORMS BAR ═══════════════════════ */}
      <section className="border-y-3 border-[#0A1A3F] bg-white py-8 sm:py-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-bold text-[#0A1A3F]/70 uppercase tracking-widest mb-6">
            Desteklenen Platformlar
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 lg:gap-8 max-w-5xl mx-auto">
            {platforms.map((p) => (
              <motion.div
                key={p.name}
                className="flex items-center justify-center gap-3 border-3 border-[#0A1A3F] px-3 sm:px-6 py-3 shadow-[3px_3px_0_0_#0A1A3F] bg-white hover:shadow-[1px_1px_0_0_#0A1A3F] hover:translate-x-[2px] hover:translate-y-[2px] transition-all cursor-default"
                whileHover={{ scale: 1.02 }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center text-white font-black text-sm"
                  style={{ backgroundColor: p.color }}
                >
                  {p.icon}
                </div>
                <span className="font-bold text-sm sm:text-base text-[#0A1A3F]">{p.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ STATS ═══════════════════════ */}
      <section className="bg-[#0A1A3F] py-12 sm:py-16 lg:py-20 border-b-3 border-[#0A1A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                className="border-3 border-[#6B8AFE]/40 bg-[#0A1A3F] p-4 sm:p-6 text-center hover:border-[#6B8AFE] transition-colors min-w-0"
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: i * 0.1 }}
              >
                <stat.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#6B8AFE] mx-auto mb-3" />
                <p className="text-2xl sm:text-4xl font-black text-white break-words">{stat.value}</p>
                <p className="text-xs sm:text-sm text-white/60 font-medium mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ HİZMET ALANLARI (hedef kelimeler) ═══════════════════════ */}
      <section className="bg-white py-12 sm:py-16 lg:py-20 border-b-3 border-[#0A1A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 px-4 py-2 mb-4 shadow-[2px_2px_0_0_#0A1A3F]">
              <span className="text-xs font-bold text-[#0A1A3F] uppercase tracking-wider">Hizmet Alanlarımız</span>
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-[#0A1A3F]">Reklam Hesabından Yönetime Tek Adres</h2>
            <p className="mt-4 text-[#0A1A3F]/70 font-medium max-w-2xl mx-auto">Hesabınız kapandıysa, limitler yetmiyorsa ya da kampanyaları uzman bir ekibe bırakmak istiyorsanız ihtiyacınıza uygun hizmeti seçin.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              { href: "/facebook-reklam-hesabi-kiralama", baslik: "Facebook Reklam Hesabı Kiralama", metin: "Meta'nın resmi iş ortaklıkları üzerinden yüksek limitli ajans reklam hesabı; aynı gün kurulum, politika ön kontrolü, %3'ten başlayan komisyon." },
              { href: "/facebook-reklam-hesabi-kiralama", baslik: "Google Ads & TikTok Ajans Hesabı", metin: "Limite takılan veya kurumsal faturalandırma isteyen markalar için kurumsal doğrulamalı Google Ads ve TikTok ajans hesapları ve bakiye yönetimi." },
              { href: "/hizmetler/reklam-yonetimi", baslik: "Reklam Yönetimi Hizmeti", metin: "Meta, Google ve TikTok kampanyalarının uzman ekiple uçtan uca yönetimi: ölçümleme, kreatif, ölçekleme ve haftalık raporlama." },
              { href: "/hizmetler/e-ticaret-reklam-yonetimi", baslik: "E-Ticaret Reklam Yönetimi", metin: "Katalog reklamları, Pixel/CAPI kurulumu, dinamik yeniden pazarlama ve ROAS odaklı bütçe yönetimi." },
              { href: "/ozellikler", baslik: "AdsManager Platformu", metin: "Tüm reklam hesaplarınızı tek panelden izleyin: otomatik raporlama, ROAS takibi, çoklu hesap ve AI destekli öneriler." },
              { href: "/hizmetler/seo-geo-hizmeti", baslik: "SEO & GEO Hizmeti", metin: "Google'da üst sıralar ve ChatGPT, Gemini, Perplexity gibi yapay zeka asistanlarında görünürlük." },
              { href: "/hizmetler/yapay-zeka-reklam-yonetimi", baslik: "Yapay Zeka ile Reklam Yönetimi", metin: "Advantage+, Performance Max ve Smart+ AI kampanyaları; yüksek limitli ajans hesabı, Pixel + CAPI sinyali ve AI kreatif üretimiyle." },
              { href: "/hizmetler/chatgpt-reklamlari", baslik: "ChatGPT Reklamları", metin: "ChatGPT'de reklam verme: hesap uygunluğu, OpenAI dönüşüm pikseli ve sunucu taraflı dönüşüm takibi; tek panelde çok kanallı rapor." },
              { href: "/hizmetler/reklam-hesabi-kapandi-cozum", baslik: "Reklam Hesabı Kapandı: Çözüm", metin: "Kapanma nedeni analizi, politika uyum düzeltmeleri ve resmi itiraz sürecinde belge ve açıklama desteği." },
            ].map((k) => (
              <Link key={k.baslik} href={k.href} className="group block border-3 border-[#0A1A3F] bg-[#FFFDF7] p-5 sm:p-6 shadow-[4px_4px_0_0_#0A1A3F] hover:shadow-[6px_6px_0_0_#6B8AFE] hover:-translate-y-0.5 transition-all">
                <h3 className="text-lg font-black text-[#0A1A3F] leading-tight">{k.baslik}</h3>
                <p className="text-sm text-[#0A1A3F]/70 font-medium leading-relaxed mt-2">{k.metin}</p>
                <span className="inline-block mt-4 text-xs font-bold uppercase tracking-wider text-[#0A1A3F] border-b-2 border-[#6B8AFE] group-hover:border-[#0A1A3F]">Detayları gör →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ FEATURES ═══════════════════════ */}
      <section className="bg-[#FFFDF7] py-14 sm:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-10 sm:mb-16" {...fadeInUp}>
            <div className="inline-flex items-center gap-2 border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 px-4 py-2 mb-4 shadow-[2px_2px_0_0_#0A1A3F]">
              <Zap size={16} className="text-[#6B8AFE]" />
              <span className="text-xs font-bold text-[#0A1A3F] uppercase tracking-wider">Özellikler</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A1A3F]">
              Reklam Yönetiminde{" "}
              <span className="text-[#6B8AFE]">Devrim</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#0A1A3F]/60 max-w-2xl mx-auto">
              Tüm dijital reklam süreçlerinizi tek bir platformdan yönetin. Zaman kazanın, performansı artırın.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                className="brutalist-card p-5 sm:p-6 hover:bg-[#6B8AFE]/5 transition-all duration-300 cursor-default group"
                {...staggerItem}
                transition={{ ...staggerItem.transition, delay: i * 0.1 }}
              >
                <div className="w-14 h-14 border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 flex items-center justify-center mb-4 shadow-[2px_2px_0_0_#0A1A3F] group-hover:bg-[#6B8AFE] group-hover:text-white transition-all">
                  <feature.icon className="w-7 h-7 text-[#6B8AFE] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-black text-[#0A1A3F] mb-2">{feature.title}</h3>
                <p className="text-[#0A1A3F]/60 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div className="text-center mt-10 sm:mt-12" {...fadeInUp}>
            <Link
              href="/ozellikler"
              className="brutalist-btn bg-white text-[#0A1A3F] inline-flex items-center justify-center gap-2 text-base sm:text-lg w-full sm:w-auto"
            >
              Tüm Özellikleri Gör
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════ HOW IT WORKS ═══════════════════════ */}
      <section className="bg-white border-y-3 border-[#0A1A3F] py-14 sm:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-10 sm:mb-16" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A1A3F]">
              Nasıl <span className="text-[#6B8AFE]">Çalışır?</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#0A1A3F]/60 max-w-2xl mx-auto">
              3 adımda reklamlarınızı profesyonelce yönetmeye başlayın.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8">
            {[
              {
                step: "01",
                title: "Hesaplarınızı Bağlayın",
                desc: "Meta, Google, TikTok ve Pinterest reklam hesaplarınızı güvenli bir şekilde AdsManager'a bağlayın.",
                icon: Layers,
              },
              {
                step: "02",
                title: "Kampanyaları Yönetin",
                desc: "Tüm kampanyalarınızı tek panelden oluşturun, düzenleyin ve optimize edin. AI önerileri ile başarıya ulaşın.",
                icon: Target,
              },
              {
                step: "03",
                title: "Sonuçları Takip Edin",
                desc: "Gerçek zamanlı raporlar ve ROAS takibi ile performansınızı ölçün. Otomatik raporları müşterilerinize gönderin.",
                icon: BarChart3,
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="relative"
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: i * 0.15 }}
              >
                <div className="brutalist-card p-6 sm:p-8 h-full">
                  <div className="text-5xl sm:text-6xl font-black text-[#6B8AFE]/20 absolute top-4 right-5 sm:right-6">
                    {item.step}
                  </div>
                  <div className="w-14 h-14 border-3 border-[#0A1A3F] bg-[#6B8AFE] flex items-center justify-center mb-6 shadow-[2px_2px_0_0_#0A1A3F]">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-black text-[#0A1A3F] mb-3">{item.title}</h3>
                  <p className="text-[#0A1A3F]/60 text-sm leading-relaxed">{item.desc}</p>
                </div>
                {/* Connector line */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-[#0A1A3F]/20" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ TESTIMONIALS ═══════════════════════ */}
      <section className="bg-[#FFFDF7] py-14 sm:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-10 sm:mb-16" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0A1A3F]">
              Müşterilerimiz{" "}
              <span className="text-[#6B8AFE]">Ne Diyor?</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#0A1A3F]/60 max-w-2xl mx-auto">
              500'den fazla marka AdsManager ile reklamlarını yönetiyor.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                className="brutalist-card p-5 sm:p-6 flex flex-col"
                {...staggerItem}
                transition={{ ...staggerItem.transition, delay: i * 0.1 }}
              >
                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                <p className="text-[#0A1A3F]/80 text-sm leading-relaxed flex-1">"{t.text}"</p>

                <div className="mt-6 pt-4 border-t-2 border-[#0A1A3F]/10 flex items-center gap-3">
                  <div className="w-10 h-10 border-2 border-[#0A1A3F] bg-gradient-to-br from-[#6B8AFE] to-[#0A1A3F] flex items-center justify-center text-white text-sm font-black">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-[#0A1A3F] text-sm">{t.name}</p>
                    <p className="text-xs text-[#0A1A3F]/50">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ BOTTOM CTA ═══════════════════════ */}
      <section className="bg-[#0A1A3F] border-t-3 border-[#0A1A3F] py-14 sm:py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <div className="inline-flex items-center gap-2 border-3 border-[#6B8AFE] bg-[#6B8AFE]/10 px-3 sm:px-4 py-2 mb-6">
              <CheckCircle2 size={16} className="text-[#6B8AFE] flex-shrink-0" />
              <span className="text-xs sm:text-sm font-bold text-[#6B8AFE] uppercase tracking-wider">
                Kurulum Ücreti Yok • Minimum Bütçe Yok
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">
              Reklamlarınızı Yönetmenin{" "}
              <span className="text-[#6B8AFE]">En Akıllı Yolu</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-white/60 max-w-xl mx-auto">
              Yüksek limitli ajans reklam hesabınızı aynı gün açalım ya da kampanyalarınızı uzman ekibimize bırakın.
              Harcama hedefinizi yazın, teklifinizi bugün iletelim.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/fiyatlar"
                className="brutalist-btn bg-[#6B8AFE] text-white border-[#6B8AFE] text-base sm:text-lg inline-flex items-center justify-center gap-2 hover:bg-[#5A7BF0] w-full sm:w-auto"
              >
                Fiyatları İnceleyin
                <ArrowRight size={20} />
              </Link>
              <a
                href="https://wa.me/905320522545?text=Merhaba%2C%20reklam%20hesab%C4%B1%20kiralama%20ve%20reklam%20y%C3%B6netimi%20i%C3%A7in%20teklif%20almak%20istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                className="brutalist-btn bg-transparent text-white border-white/30 text-base sm:text-lg inline-flex items-center justify-center gap-2 hover:border-white w-full sm:w-auto"
              >
                <MessageCircle size={18} />
                WhatsApp'tan Teklif Alın
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
